import { Stack, Typography } from '@mui/material';
import { FilterValue } from '@schooly/api';
import { useConfirmationDialog } from '@schooly/components/confirmation-dialog';
import { SchoolPropertyType, SchoolUserRole } from '@schooly/constants';
import { useSchoolProperties } from '@schooly/hooks/use-school-properties';
import { TagSelect, TagSelectProperty } from '@schooly/style';
import { FC, useCallback, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { DropdownSelect, DropdownSelectProps } from '../DropdownSelect';
import { SelectContentSkeleton } from '../SelectContentSkeleton';
import { SelectOptionsArchivedIcon } from '../SelectOptionsArchivedIcon';
import { SelectSearchInput } from '../SelectSearchInput';
import { filterPropertiesByQuery } from '../utils';
import { DottedDivider } from './DottedDivider.styled';
import { renderPropertyTypeTags } from './PropertyTypeExpandedSelect';

type PropertyTypeSelectMultipleProps = {
  hideIfNoOptions?: boolean;
  propertyType: SchoolPropertyType;
  userRole: SchoolUserRole;
  schoolId: string;
  optionsTitle?: string;
  isAdmin?: boolean;
  allowArchived?: boolean;
  availableIds?: FilterValue[];
  required?: boolean;
} & Omit<DropdownSelectProps, 'children' | 'renderContent'> &
  (
    | { onSelectId: (v: string) => void; selectedIds: string[] }
    | {
        onSelectId: (v: string | null) => void;
        noneOptionLabel: string;
        selectedIds: string[] | null;
      }
  );
export const PropertyTypeSelectMultiple: FC<PropertyTypeSelectMultipleProps> = (props) => {
  const {
    hideIfNoOptions,
    optionsTitle,
    schoolId,
    selectedIds,
    propertyType,
    userRole,
    placeholder,
    onSelectId,
    isAdmin,
    allowArchived,
    availableIds,
    required,
    ...dropdownProps
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdown = useRef<DropdownSelect | null>(null);
  const [query, setQuery] = useState('');

  const [queryEnabled, setQueryEnabled] = useState(false);

  const { getConfirmation } = useConfirmationDialog();
  const { propertiesMap, activePropertiesMap, archivedPropertiesMap, isLoading, getPropertyById } =
    useSchoolProperties({ schoolId, userType: userRole }, { enabled: queryEnabled });
  const isNoneOptionSelected = selectedIds === null;

  const hasNoneOption = 'noneOptionLabel' in props;
  const noneOptionLabel = hasNoneOption ? props.noneOptionLabel : undefined;

  const isDisabled = useCallback(
    (id: string) => !!required && selectedIds?.length === 1 && selectedIds.includes(id),
    [required, selectedIds],
  );

  const showAllArchivedRightIcon =
    !!selectedIds &&
    !allowArchived &&
    !selectedIds.length &&
    !activePropertiesMap[propertyType].length &&
    archivedPropertiesMap[propertyType].length > 0;

  const renderAllArchivedIcon = useCallback(
    () => <SelectOptionsArchivedIcon isAdmin={!!isAdmin} type={propertyType} />,
    [isAdmin, propertyType],
  );

  const handleSelectPropertyId = useCallback(
    async (id: string) => {
      const type = getPropertyById(id);

      if (
        !allowArchived &&
        type?.archived &&
        !(await getConfirmation({
          textId: `deselect-property-archived-${propertyType}`,
          textValues: { name: type.name },
        }))
      ) {
        return;
      }
      onSelectId(id);
    },
    [allowArchived, getConfirmation, getPropertyById, onSelectId, propertyType],
  );

  const handleSelectId = useCallback(
    (v: string | null) => {
      hasNoneOption ? props.onSelectId(v) : v && handleSelectPropertyId(v);
    },
    [hasNoneOption, props, handleSelectPropertyId],
  );

  const renderContent = useCallback(() => {
    if (isLoading) return <SelectContentSkeleton />;

    const filterByAvailable = <T extends { id: string }>(items: T[]) =>
      availableIds ? items.filter((item) => availableIds.includes(item.id)) : items;

    const activeProperties = filterByAvailable(
      filterPropertiesByQuery(query, activePropertiesMap[propertyType]),
    );
    const archivedProperties = filterByAvailable(
      filterPropertiesByQuery(query, archivedPropertiesMap[propertyType]),
    );

    if (!activeProperties.length && !archivedProperties.length)
      return (
        <Typography p={1}>
          <FormattedMessage id="input-NoOptionsFound" />
        </Typography>
      );

    return (
      <>
        <Stack direction="row" flexWrap="wrap" gap={1} m={1}>
          {activeProperties.map((property) => (
            <TagSelectProperty
              key={property.id}
              userRole={userRole}
              property={property}
              disabled={isDisabled(property.id)}
              variant={selectedIds?.includes(property.id) ? 'filled' : undefined}
              onClick={() => {
                onSelectId(property.id);
                setQuery('');
              }}
            />
          ))}
        </Stack>
        {(hasNoneOption || (allowArchived && !!archivedProperties.length)) && (
          <>
            <DottedDivider />
            <Stack direction="row" flexWrap="wrap" gap={1} m={1}>
              {hasNoneOption && (
                <TagSelect
                  label={noneOptionLabel}
                  variant={isNoneOptionSelected ? 'filled' : undefined}
                  onClick={() => {
                    handleSelectId(null);
                    setQuery('');
                  }}
                />
              )}
              {archivedProperties.map((property) => (
                <TagSelectProperty
                  key={property.id}
                  userRole={userRole}
                  property={property}
                  disabled={isDisabled(property.id)}
                  variant={selectedIds?.includes(property.id) ? 'filled' : undefined}
                  onClick={() => {
                    onSelectId(property.id);
                    setQuery('');
                  }}
                />
              ))}
            </Stack>
          </>
        )}
      </>
    );
  }, [
    activePropertiesMap,
    allowArchived,
    archivedPropertiesMap,
    availableIds,
    handleSelectId,
    hasNoneOption,
    isDisabled,
    isLoading,
    isNoneOptionSelected,
    noneOptionLabel,
    onSelectId,
    propertyType,
    query,
    selectedIds,
    userRole,
  ]);

  if (hideIfNoOptions && !isLoading && !propertiesMap[propertyType].length) return null;

  return (
    <DropdownSelect
      {...dropdownProps}
      ref={dropdown}
      onToggle={setQueryEnabled}
      placeholder={placeholder}
      hasValues={selectedIds === null || selectedIds.length > 0}
      renderContent={renderContent}
      renderRightIcon={showAllArchivedRightIcon ? renderAllArchivedIcon : undefined}
      onClickInputArea={() => inputRef.current?.focus()}
    >
      {(opened) => (
        <>
          {isNoneOptionSelected && hasNoneOption ? (
            <TagSelect
              label={props.noneOptionLabel}
              sx={{ maxWidth: 200 }}
              onDelete={() => handleSelectId(null)}
              size="small"
            />
          ) : (
            renderPropertyTypeTags({
              ids: selectedIds ?? [],
              tagProps: (id) => {
                const disabled = isDisabled(id);
                return {
                  userRole,
                  schoolId,
                  size: placeholder ? 'small' : undefined,
                  sx: { maxWidth: 200 },
                  disabled,
                  ...(disabled ? { onDelete: undefined } : {}),
                };
              },
              onDelete: opened ? handleSelectId : undefined,
            })
          )}

          {opened && (
            <SelectSearchInput
              ref={inputRef}
              autoFocus
              value={query || ''}
              onChangeText={setQuery}
            />
          )}
        </>
      )}
    </DropdownSelect>
  );
};
